import { Injectable, inject, effect, untracked, Injector, runInInjectionContext } from '@angular/core';
import { GraphQLClientService } from './graphql-client.service';
import { ViewerIdentityService } from './viewer-identity.service';
import { DietaryPreferencesService, DietaryPreferences } from './dietary-preferences.service';
import { VehiclesService, Vehicle } from './vehicles.service';
import { UserPreferencesService, type CommuteMode } from './user-preferences.service';
import { ThemeService, type ThemeName, type FontName } from './theme.service';
import { isOk } from '../models/api-result';

/**
 * RESPONSIBILITY: Cross-device sync for the PROFILE settings that previously
 * lived in localStorage only — dietary preferences, registered vehicles,
 * workplace/commute preferences, and the appearance axes (theme, font,
 * personal brand accent). localStorage stays the offline-first cache; this
 * service makes the data follow the signed-in user across devices via the
 * backend stores:
 *
 *   - `myProfileSettings` / `updateProfileSettings` — dietary + vehicles +
 *     workplace sections on the ProfileSettings collection (@Auth).
 *   - `updateProfile` (theme, font, brandColorOverride) — appearance lives
 *     on the User document next to the existing theme/font fields; hydrate
 *     reads them straight off the viewer query.
 *
 * PATTERN: mirrors {@link MapPreferencesSyncService} (hydrate → backend
 * wins → first-boot migration pushes local up → write-through effect
 * mirrors local changes to the server; echo suppressed via a lastSynced
 * baseline). See that service for the rationale on keeping sync out of
 * UserPreferencesService (circular-dependency risk).
 *
 * LIFECYCLE:
 *   hydrate()  — call on boot after the viewer is loaded. No-op for
 *                anonymous/demo viewers.
 *   reset()    — on logout: stop write-through and clear the personal
 *                stores (dietary + vehicles) so the next viewer on a shared
 *                device can't inherit them; the data returns on next
 *                sign-in via hydrate. (UserPreferences reset is owned by
 *                MapPreferencesSyncService.reset, which logout also calls.)
 */

const MY_PROFILE_SETTINGS_QUERY = `
  query myProfileSettings {
    myProfileSettings {
      _id
      dietary { dietaryTags allergenExclusions autoApplyFilters setupComplete }
      vehicles { id registration make model colour vehicleType isElectric isDefault addedAt }
      workplace {
        homeLocationId homeAddressLabel homeCoordinates homeMapboxId
        usualTravelMode preferredCommuteMode preferredArriveBy workingDays
      }
      updatedAt
    }
  }
`;

const UPDATE_PROFILE_SETTINGS_MUTATION = `
  mutation updateProfileSettings($input: UpdateProfileSettingsInput!) {
    updateProfileSettings(input: $input) { _id updatedAt }
  }
`;

/** Appearance-only slice of the existing self-service profile mutation. */
const UPDATE_APPEARANCE_MUTATION = `
  mutation updateProfile($employee: EmployeeInput!) {
    updateProfile(employee: $employee) { _id theme font brandColorOverride }
  }
`;

interface ProfileSettingsDto {
  _id?: string;
  dietary?: {
    dietaryTags?: string[] | null;
    allergenExclusions?: string[] | null;
    autoApplyFilters?: boolean | null;
    setupComplete?: boolean | null;
  } | null;
  vehicles?: Array<{
    id: string;
    registration: string;
    make?: string | null;
    model?: string | null;
    colour?: string | null;
    vehicleType: string;
    isElectric: boolean;
    isDefault: boolean;
    addedAt?: string | null;
  }> | null;
  workplace?: {
    homeLocationId?: string | null;
    homeAddressLabel?: string | null;
    homeCoordinates?: number[] | null;
    homeMapboxId?: string | null;
    usualTravelMode?: string | null;
    preferredCommuteMode?: string | null;
    preferredArriveBy?: string | null;
    workingDays?: number[] | null;
  } | null;
}

/** The workplace/commute keys of UserPreferences this service syncs. */
interface WorkplacePrefs {
  homeLocationId: string | null;
  homeAddressLabel: string | null;
  homeCoordinates: [number, number] | null;
  homeMapboxId: string | null;
  usualTravelMode: string | null;
  preferredCommuteMode: string;
  preferredArriveBy: string | null;
  workingDays: number[];
}

/** Appearance axes synced onto the User document. */
export interface AppearancePrefs {
  theme: string;
  font: string;
  brandColorOverride: string | null;
}

/**
 * The appearance fields that changed since `prev`, as the sparse
 * `updateProfile` input, or null when nothing did (or there is no baseline
 * yet). Only what changed is sent: a theme toggle must never write a font the
 * profile never chose. An accent cleared to null is sent as `''`, which the
 * API reads as "clear" (undefined would mean "don't touch").
 */
export function appearanceDelta(
  prev: AppearancePrefs | null,
  next: AppearancePrefs,
): Record<string, string> | null {
  if (!prev) return null;
  const delta: Record<string, string> = {};
  if (next.theme !== prev.theme) delta['theme'] = next.theme;
  if (next.font !== prev.font) delta['font'] = next.font;
  if ((next.brandColorOverride ?? '') !== (prev.brandColorOverride ?? '')) {
    delta['brandColorOverride'] = next.brandColorOverride ?? '';
  }
  return Object.keys(delta).length ? delta : null;
}

const WORKPLACE_DEFAULTS: WorkplacePrefs = {
  homeLocationId: null,
  homeAddressLabel: null,
  homeCoordinates: null,
  homeMapboxId: null,
  usualTravelMode: null,
  preferredCommuteMode: 'driving',
  preferredArriveBy: '09:00',
  workingDays: [0, 1, 2, 3, 4],
};

@Injectable({ providedIn: 'root' })
export class ProfileSettingsSyncService {
  private readonly graphql = inject(GraphQLClientService);
  private readonly identity = inject(ViewerIdentityService);
  private readonly dietary = inject(DietaryPreferencesService);
  private readonly vehicles = inject(VehiclesService);
  private readonly prefs = inject(UserPreferencesService);
  private readonly theme = inject(ThemeService);
  private readonly injector = inject(Injector);

  /** Write-through stays dormant until a hydrate has established a baseline. */
  private hydrated = false;

  private writeThroughRef: { destroy(): void } | null = null;

  /**
   * Appearance write-back is on once the viewer's appearance has been folded
   * in, whatever happens to the `myProfileSettings` read after it: the theme,
   * font and accent ride on the viewer document, so a failed settings read
   * must not leave them device-local.
   */
  private appearanceHydrated = false;

  /**
   * Hydrate as soon as a viewer exists, however the shell got one: the root
   * load, a guard's reload after the token exchange, a later retry. A first
   * viewer load that 401s while the token bootstrap is still racing used to
   * mean hydration never ran at all. Ported from the employee app
   * (`hydrateWhenIdentityArrives`). A viewer that changes while hydration has
   * not completed (the settings read failed) tries again.
   */
  private readonly identityWatch = this.hydrateWhenIdentityArrives();

  /** JSON baselines per section — dedupes the effect + suppresses echoes. */
  private lastDietary: string | null = null;
  private lastVehicles: string | null = null;
  private lastWorkplace: string | null = null;
  /** The appearance last sent or read, per field: what a change is diffed against. */
  private lastAppearance: AppearancePrefs | null = null;

  private isAuthenticated(): boolean {
    return !!this.identity.viewer()?._id;
  }

  // ── Snapshots ───────────────────────────────────────────

  private currentDietary(): Omit<DietaryPreferences, 'setupComplete'> & { setupComplete: boolean } {
    return this.dietary.snapshot();
  }

  private currentVehicles(): Vehicle[] {
    return this.vehicles.snapshot();
  }

  private currentWorkplace(): WorkplacePrefs {
    const p = this.prefs.snapshot();
    return {
      homeLocationId: p.homeLocationId,
      homeAddressLabel: p.homeAddressLabel,
      homeCoordinates: p.homeCoordinates,
      homeMapboxId: p.homeMapboxId,
      usualTravelMode: p.usualTravelMode,
      preferredCommuteMode: p.preferredCommuteMode,
      preferredArriveBy: p.preferredArriveBy,
      workingDays: [...p.workingDays],
    };
  }

  private currentAppearance(): AppearancePrefs {
    return {
      theme: this.theme.theme(),
      font: this.theme.font(),
      brandColorOverride: this.prefs.brandColorOverride(),
    };
  }

  // ── Hydrate ─────────────────────────────────────────────

  /** Single-flight guard — multiple boot paths (viewer boot, schedule's
   *  location init) may request hydration concurrently; only one query runs. */
  private hydrateInFlight: Promise<void> | null = null;

  /**
   * Pull the backend profile settings and fold them into the local stores.
   * Backend wins per section; a section the server doesn't have yet is
   * seeded from this device (first-boot migration). Idempotent; no-op for
   * anonymous viewers; a failed pull leaves local state untouched and does
   * NOT arm write-through (so a transient error can't clobber anything).
   */
  async hydrate(): Promise<void> {
    if (this.hydrated) return;
    this.hydrateInFlight ??= this.doHydrate().finally(() => {
      this.hydrateInFlight = null;
    });
    return this.hydrateInFlight;
  }

  /**
   * Wait for hydrate() to land, but never longer than `timeoutMs`. Used by
   * boot paths that want the cross-device profile values (e.g. the home
   * location) when they arrive quickly, without stalling the page on a slow
   * round-trip. Errors are swallowed — callers proceed with local state.
   */
  async hydrateWithin(timeoutMs: number): Promise<void> {
    await Promise.race([
      this.hydrate().catch(() => {}),
      new Promise<void>((resolve) => setTimeout(resolve, timeoutMs)),
    ]);
  }

  private hydrateWhenIdentityArrives(): { destroy(): void } | null {
    try {
      return runInInjectionContext(this.injector, () =>
        effect(() => {
          const id = this.identity.viewer()?._id;
          untracked(() => {
            if (!id || this.hydrated) return;
            void this.hydrate().catch(() => {});
          });
        }),
      );
    } catch (e) {
      // No change-detection scheduler in this injection context (an exotic
      // test harness). An explicit hydrate() still works; only the watch is lost.
      console.warn('[ProfileSettingsSync] identity watch unavailable:', e);
      return null;
    }
  }

  private async doHydrate(): Promise<void> {
    if (!this.isAuthenticated()) {
      this.hydrated = false;
      return;
    }

    // Appearance rides on the viewer document — no extra round-trip. Folded in
    // once: a retry after a failed settings read must not put the viewer's
    // (now stale) appearance back over a change made since.
    if (!this.appearanceHydrated) {
      this.hydrateAppearanceFromViewer();
      // Write-back for appearance is on from here, baselined per field.
      this.lastAppearance = this.currentAppearance();
      this.appearanceHydrated = true;
      this.armWriteThrough();
    }

    const res = await this.graphql.queryResult<ProfileSettingsDto>(
      MY_PROFILE_SETTINGS_QUERY,
      undefined,
      'network-only',
    );
    if (!isOk(res)) return;

    const remote = res.data;
    const migrate: Record<string, unknown> = {};

    // Dietary — backend wins when present.
    if (remote?.dietary) {
      this.dietary.update({
        dietaryTags: remote.dietary.dietaryTags ?? [],
        allergenExclusions: remote.dietary.allergenExclusions ?? [],
        autoApplyFilters: remote.dietary.autoApplyFilters ?? true,
        setupComplete: remote.dietary.setupComplete ?? false,
      });
    } else if (this.currentDietary().setupComplete) {
      migrate['dietary'] = this.dietaryInput();
    }

    // Vehicles — backend wins when present (including an empty fleet).
    if (remote?.vehicles) {
      this.vehicles.replaceAll(remote.vehicles.map(v => ({
        id: v.id,
        registration: v.registration,
        make: v.make ?? '',
        model: v.model ?? '',
        colour: v.colour ?? null,
        type: (v.vehicleType as Vehicle['type']) ?? 'car',
        isElectric: v.isElectric,
        isDefault: v.isDefault,
        addedAt: v.addedAt ?? new Date().toISOString(),
      })));
    } else if (this.currentVehicles().length > 0) {
      migrate['vehicles'] = this.vehiclesInput();
    }

    // Workplace — backend wins when present.
    if (remote?.workplace) {
      const w = remote.workplace;
      const coords = Array.isArray(w.homeCoordinates) && w.homeCoordinates.length === 2
        ? ([w.homeCoordinates[0], w.homeCoordinates[1]] as [number, number])
        : null;
      const commuteModes: CommuteMode[] = ['driving', 'walking', 'cycling', 'transit'];
      this.prefs.update({
        homeLocationId: w.homeLocationId ?? null,
        homeAddressLabel: w.homeAddressLabel ?? null,
        homeCoordinates: coords,
        homeMapboxId: w.homeMapboxId ?? null,
        usualTravelMode: w.usualTravelMode ?? null,
        ...(commuteModes.includes(w.preferredCommuteMode as CommuteMode)
          ? { preferredCommuteMode: w.preferredCommuteMode as CommuteMode }
          : {}),
        preferredArriveBy: w.preferredArriveBy ?? WORKPLACE_DEFAULTS.preferredArriveBy,
        workingDays: w.workingDays ?? [...WORKPLACE_DEFAULTS.workingDays],
      });
    } else if (JSON.stringify(this.currentWorkplace()) !== JSON.stringify(WORKPLACE_DEFAULTS)) {
      migrate['workplace'] = this.workplaceInput();
    }

    // First-boot migration — seed the server from this device's values.
    if (Object.keys(migrate).length > 0) {
      this.pushSettings(migrate).catch(() => {});
    }

    // Baseline + arm write-through.
    this.lastDietary = JSON.stringify(this.dietaryInput());
    this.lastVehicles = JSON.stringify(this.vehiclesInput());
    this.lastWorkplace = JSON.stringify(this.workplaceInput());
    this.armWriteThrough();
    this.hydrated = true;
  }

  /**
   * Apply the viewer document's appearance fields (theme / font / brand
   * accent). Backend wins when set; when unset but this device is
   * customised, push the local values up so the record is seeded.
   */
  private hydrateAppearanceFromViewer(): void {
    const viewer = this.identity.viewer() as (ReturnType<ViewerIdentityService['viewer']> & {
      theme?: string | null;
      font?: string | null;
      brandColorOverride?: string | null;
    }) | null;
    if (!viewer) return;

    const local = this.currentAppearance();
    const push: Record<string, string> = {};

    if (viewer.theme) {
      if (viewer.theme !== local.theme) this.theme.setTheme(viewer.theme as ThemeName);
    } else if (local.theme !== 'workplace-paper') {
      push['theme'] = local.theme;
    }

    if (viewer.font) {
      if (viewer.font !== local.font) this.theme.setFont(viewer.font as FontName);
    } else if (local.font !== 'inter') {
      push['font'] = local.font;
    }

    if (viewer.brandColorOverride != null && viewer.brandColorOverride !== '') {
      if (viewer.brandColorOverride !== local.brandColorOverride) {
        this.prefs.update({ brandColorOverride: viewer.brandColorOverride });
      }
    } else if (local.brandColorOverride) {
      push['brandColorOverride'] = local.brandColorOverride;
    }

    if (Object.keys(push).length > 0) {
      this.pushAppearance(push).catch(() => {});
    }
  }

  /**
   * Push the CURRENT appearance to the server immediately. Called from
   * explicit user gestures (accent picker) so the save never depends on
   * the boot-armed write-through effect — deep links and boot paths that
   * bail early (e.g. missing map token) would otherwise leave appearance
   * changes device-local. Baselines `lastAppearance` so the write-through
   * doesn't double-push. Fire-and-forget; errors are swallowed
   * (localStorage already holds the value).
   */
  pushAppearanceNow(): void {
    if (!this.isAuthenticated() || !this.appearanceHydrated) return;
    const appearance = this.currentAppearance();
    // Only the fields that changed since the last send or read.
    const delta = appearanceDelta(this.lastAppearance, appearance);
    if (!delta) return;
    this.lastAppearance = appearance;
    this.pushAppearance(delta).catch(() => {});
  }

  // ── Logout ──────────────────────────────────────────────

  /**
   * Sign-out: stop write-through, drop baselines, and clear the personal
   * stores (dietary + vehicles) so a shared device doesn't leak them to
   * the next viewer. Their values live on the server now and return on the
   * next sign-in. UserPreferences (workplace/commute) reset is handled by
   * MapPreferencesSyncService.reset() on the same logout path.
   */
  reset(): void {
    this.dropSyncBaseline();
    this.dietary.reset();
    this.vehicles.reset();
  }

  /**
   * Customer switch: drop the sync baseline WITHOUT resetting the personal
   * stores. Same contract as {@link MapPreferencesSyncService.dropSyncBaseline}
   * — `reset()` is the logout path.
   *
   * The stored VALUES are customer-scoped (ProfileSettings is tenant-scoped
   * server-side, so dietary and vehicles belong to one employee row), but
   * they are cleared by the switcher's customer-scoped storage sweep rather
   * than here, alongside every other key of that kind. What this drops is the
   * comparison baseline and the write-through, so the next `hydrate()` pulls
   * the destination customer's values instead of diffing against the previous
   * customer's and pushing the difference back up.
   */
  dropSyncBaseline(): void {
    this.hydrated = false;
    this.appearanceHydrated = false;
    this.lastDietary = null;
    this.lastVehicles = null;
    this.lastWorkplace = null;
    this.lastAppearance = null;
    this.writeThroughRef?.destroy();
    this.writeThroughRef = null;
  }

  // ── Write-through ───────────────────────────────────────

  private armWriteThrough(): void {
    if (this.writeThroughRef) return;
    try {
      this.createWriteThroughEffect();
    } catch (e) {
      // No change-detection scheduler in this injection context (exotic test
      // harness) — localStorage still holds every change; only the
      // cross-device push is disabled. Never let this break boot hydration.
      console.warn('[ProfileSettingsSync] write-through effect unavailable:', e);
    }
  }

  private createWriteThroughEffect(): void {
    runInInjectionContext(this.injector, () => {
      this.writeThroughRef = effect(() => {
        // Track the synced stores.
        this.dietary.preferences();
        this.vehicles.vehicles();
        this.prefs.preferences();
        this.theme.theme();
        this.theme.font();

        untracked(() => this.flushWriteThrough());
      });
    });
  }

  /**
   * Send what changed since the last baseline. The profile-settings sections
   * wait for a completed hydrate; appearance needs only the viewer's, so its
   * write-back is on even when the settings read failed. Appearance sends only
   * the fields that changed (`appearanceDelta`).
   */
  private flushWriteThrough(): void {
    if (!this.isAuthenticated()) return;

    if (this.hydrated) {
      const input: Record<string, unknown> = {};
      const dietaryNow = JSON.stringify(this.dietaryInput());
      if (dietaryNow !== this.lastDietary) {
        this.lastDietary = dietaryNow;
        input['dietary'] = this.dietaryInput();
      }
      const vehiclesNow = JSON.stringify(this.vehiclesInput());
      if (vehiclesNow !== this.lastVehicles) {
        this.lastVehicles = vehiclesNow;
        input['vehicles'] = this.vehiclesInput();
      }
      const workplaceNow = JSON.stringify(this.workplaceInput());
      if (workplaceNow !== this.lastWorkplace) {
        this.lastWorkplace = workplaceNow;
        input['workplace'] = this.workplaceInput();
      }
      if (Object.keys(input).length > 0) {
        this.pushSettings(input).catch(() => {
          // Network hiccup — localStorage already holds the value; the
          // next change retries.
        });
      }
    }

    if (this.appearanceHydrated) {
      const appearance = this.currentAppearance();
      const delta = appearanceDelta(this.lastAppearance, appearance);
      if (delta) {
        this.lastAppearance = appearance;
        this.pushAppearance(delta).catch(() => {});
      }
    }
  }

  // ── Input builders (client → GraphQL input shapes) ─────

  private dietaryInput(): Record<string, unknown> {
    const d = this.currentDietary();
    return {
      dietaryTags: d.dietaryTags,
      allergenExclusions: d.allergenExclusions,
      autoApplyFilters: d.autoApplyFilters,
      setupComplete: d.setupComplete,
    };
  }

  private vehiclesInput(): Record<string, unknown>[] {
    return this.currentVehicles().map(v => ({
      id: v.id,
      registration: v.registration,
      make: v.make || undefined,
      model: v.model || undefined,
      colour: v.colour ?? undefined,
      vehicleType: v.type,
      isElectric: v.isElectric,
      isDefault: v.isDefault,
      addedAt: v.addedAt,
    }));
  }

  private workplaceInput(): Record<string, unknown> {
    const w = this.currentWorkplace();
    return {
      homeLocationId: w.homeLocationId ?? undefined,
      homeAddressLabel: w.homeAddressLabel ?? undefined,
      homeCoordinates: w.homeCoordinates ?? undefined,
      homeMapboxId: w.homeMapboxId ?? undefined,
      usualTravelMode: w.usualTravelMode ?? undefined,
      preferredCommuteMode: w.preferredCommuteMode,
      preferredArriveBy: w.preferredArriveBy ?? undefined,
      workingDays: w.workingDays,
    };
  }

  // ── Pushes ──────────────────────────────────────────────

  private async pushSettings(input: Record<string, unknown>): Promise<void> {
    if (!this.isAuthenticated()) return;
    await this.graphql.mutateResult(UPDATE_PROFILE_SETTINGS_MUTATION, { input });
  }

  private async pushAppearance(employee: Record<string, string>): Promise<void> {
    if (!this.isAuthenticated()) return;
    await this.graphql.mutateResult(UPDATE_APPEARANCE_MUTATION, { employee });
  }
}
